import { IdmsErrorTable } from '@/components/Analysis/Idms/IdmsErrorTable';
import { useAtom } from 'jotai';
import * as state from '../state';
import { csvStringToJson, xlsxBase64ToJson } from '@/components/Analysis/Idms/utils';
import { TemplateValidationError } from '@/components/Analysis/Idms/types';
import { Box, Button, IconButton, Modal, Paper, Stack, Typography } from '@mui/material';
import { ValidationsErrorDialogStyles } from '@/components/Analysis/modules/pointOfInterest/BulkUpload/components/styles';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';

export const PipeSpecUploadErrorTableModal = () => {
  const [pipeSpecUploadState, setPipeSpecUploadState] = useAtom(state.pipeSpecUploadState);
  const [parsedData, setParsedData] = useState<{ header: string[]; rows: Record<string, unknown>[] }>({
    header: [],
    rows: []
  });

  const isOpen = pipeSpecUploadState.state === 'csv-error';
  const error = pipeSpecUploadState.error?.extensions?.error;

  useEffect(() => {
    const parseData = async () => {
      if (!pipeSpecUploadState.inputCsv || pipeSpecUploadState.state !== 'csv-error') {
        return;
      }
      
      try {
        // Check if it's XLSX format (base64 starts with 'UEsDB' which is the ZIP header for XLSX)
        // or if it has the data URL prefix
        const isXlsx =
          pipeSpecUploadState.inputCsv.startsWith('data:application/vnd.openxmlformats') ||
          pipeSpecUploadState.inputCsv.startsWith('UEsDB');

        if (isXlsx) {
          const result = await xlsxBase64ToJson(pipeSpecUploadState.inputCsv);

          setParsedData({
            header: result.header,
            rows: result.rows,
          });
        } else {
          const result = csvStringToJson(pipeSpecUploadState.inputCsv);
          setParsedData({
            header: result.header,
            rows: result.rows,
          });
        }
      } catch (err) {
        setParsedData({
          header: [],
          rows: [],
        });
      }
    };
    
    parseData();
  }, [pipeSpecUploadState.inputCsv, pipeSpecUploadState.state]);

  // No error means no need to show the modal
  if (!error || !pipeSpecUploadState.inputCsv || pipeSpecUploadState.state !== 'csv-error') {
    return <></>;
  }

  const onClose = () => {
    setPipeSpecUploadState({
      ...pipeSpecUploadState,
      state: 'idle',
      error: undefined,
      inputCsv: undefined,
    });
  };

  const triggerReupload = () => {
    pipeSpecUploadState.inputRef?.click?.();
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Paper sx={ValidationsErrorDialogStyles}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography fontWeight={'bold'} fontSize={20} my={2}>
            The file contains errors
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography sx={{ mb: 1 }}>
          Note: This file cannot be uploaded due to exceptions highlighted in the table below. Hover
          over each highlighted value to view the specific exception.
        </Typography>
        <IdmsErrorTable
          header={parsedData.header}
          rows={parsedData.rows}
          errors={pipeSpecUploadState.error?.extensions?.error as Array<TemplateValidationError>}
          footer={
            <Box sx={{ mt: 1, width: '100%', display: 'flex' }} justifyContent={'flex-end'}>
              <Button id="pipe-spec-reupload-cancel-button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                id="pipe-spec-reupload-button"
                onClick={triggerReupload}
                variant="contained"
                color="primary"
                startIcon={<FileUploadIcon />}
                sx={{ ml: 2 }}
              >
                Re Upload
              </Button>
            </Box>
          }
        />
      </Paper>
    </Modal>
  );
};
